import { BatchRun, count } from '../../shared'
import { BatchPhaseStep } from '../../../../reusable/batchWidgetCard/BatchWidgetCard'

export const STATUS_POLL_MS = 2_500
// A batch keeps running server-side after the page unmounts, so on mount we
// look for one still in flight and rebuild the running card around it.
export const RESUME_LOOKBACK = 10

/** Stop trusting the running card once status calls have failed for this long. */
export const LOST_CONTACT_AFTER_MS = 30_000

/** The runner beats every 30s, so an unchanged heartbeat this long means it is gone. */
export const STALLED_AFTER_MS = 3 * 60_000

export interface BatchStartResponse {
    success: boolean
    batchId: string
    total: number
    invalid: number
    environment?: string
    message?: string
}

export interface BatchPreviewMissingField {
    key: string
    label: string
    count: number
    effect: 'transunion' | 'skipped'
    summary: string
}

export interface BatchPreviewCoverage {
    success: boolean
    total: number
    invalid: number
    equifaxValid: number
    transUnionValid: number
    missingTitle: number
    missingFields?: BatchPreviewMissingField[]
    message?: string
}

export const isSpreadsheetName = (name: string) => /\.(csv|xlsx|xls)$/.test(name.toLowerCase())

export const OPTIMISTIC_BATCH_PREFIX = 'optimistic-'

export const isOptimisticBatchId = (id: string) => id.startsWith(OPTIMISTIC_BATCH_PREFIX)

/** Placeholder history row shown the moment Go is pressed, before the API returns. */
export const createOptimisticBatchRun = (
    filename: string | null,
    origin: string | null = null
): BatchRun => ({
    id: `${OPTIMISTIC_BATCH_PREFIX}${Date.now()}`,
    created_at: new Date().toISOString(),
    run_started_at: new Date().toISOString(),
    created_by: null,
    finished_at: null,
    duration_seconds: 0,
    status: 'running',
    environment: null,
    filename,
    origin,
    total_count: null,
    seeded_count: 0,
    processed_count: 0,
    invalid_count: 0,
    success_count: 0,
    duplicate_count: 0,
    pdf_pending_count: 0,
    blocked_count: 0,
    challenge_count: 0,
    error_count: 0,
    results_key: null,
    error_message: null,
    updated_at: null,
})

export const applyBatchStartToOptimistic = (run: BatchRun, batch: BatchStartResponse): BatchRun => ({
    ...run,
    id: batch.batchId,
    total_count: batch.total,
    invalid_count: batch.invalid,
    environment: batch.environment ?? run.environment,
    status: 'running',
})

export const mergeOptimisticRun = (runs: BatchRun[], optimistic: BatchRun | null) => {
    if (!optimistic) return runs
    if (runs.some((run) => run.id === optimistic.id)) return runs
    return [optimistic, ...runs]
}

/**
 * A run spends its first stretch checking for duplicates and seeding rows, with
 * no rows processed yet. Without this the bar sits on "0 of N rows" throughout.
 */
const PHASE_LABELS: Record<string, string> = {
    deduping: 'Checking for duplicates',
    seeding: 'Preparing rows',
}

/**
 * Stretches that fill the progress bar. Duplicate check sits on the verifying
 * panel (text only, no bar or pills) because it has no row progress and is brief.
 */
export type ProgressPhase = 'deduping' | 'seeding' | 'processing'
export type BadgePhase = 'seeding' | 'processing'

export const PHASE_STEPS: { key: BadgePhase; label: string }[] = [
    { key: 'seeding', label: 'Preparing rows' },
    { key: 'processing', label: 'Credit checks' },
]

/** Pills for the bar stretches only. Empty while verifying or checking duplicates. */
export const phaseStepsFor = (phase: ProgressPhase | null): BatchPhaseStep[] => {
    if (phase !== 'seeding' && phase !== 'processing') return []
    const current = PHASE_STEPS.findIndex((step) => step.key === phase)
    return PHASE_STEPS.map((step, index) => ({
        label: step.label,
        state: index < current ? 'done' : index === current ? 'active' : 'pending',
    }))
}

export interface ProgressSample {
    at: number
    /** processed_count + invalid_count, ie rows the run is finished with. */
    done: number
}

/**
 * Enough of a sample to be worth showing. Duplicates finish instantly, so the
 * first handful of rows can imply a rate the rest of the run cannot hold.
 */
export const MIN_ROWS_FOR_ETA = 25
export const MIN_MS_FOR_ETA = 30_000

/**
 * Time left, from the rate measured between the anchor and the latest poll.
 *
 * The anchor is the first sample that showed progress rather than the batch's
 * created_at: a run starts with a dedupe/seed stretch that completes no rows,
 * and a resumed run keeps a created_at from hours earlier. Either would drag
 * the measured rate down and overstate what is left.
 *
 * Averaging over the whole window rather than the last poll keeps the number
 * steady, and absorbs the duplicate/full-row mix since both are in the rate.
 *
 * Pass the sample where `done` last changed, NOT the latest poll. Polls are
 * 2.5s apart and the backend publishes every 25 rows, so a fresh timestamp
 * against a stalled row count divides a growing elapsed by fixed rows — the
 * estimate inflates for ~110s, then snaps back when the checkpoint lands.
 *
 * @returns milliseconds remaining, or null while there is too little to say
 */
export const etaMsRemaining = (
    anchor: ProgressSample | null,
    latest: ProgressSample,
    total: number
): number | null => {
    if (!anchor || total <= 0) return null
    const rows = latest.done - anchor.done
    const elapsed = latest.at - anchor.at
    if (rows < MIN_ROWS_FOR_ETA || elapsed < MIN_MS_FOR_ETA) return null
    const remaining = total - latest.done
    if (remaining <= 0) return 0
    return Math.round((elapsed / rows) * remaining)
}

/**
 * Tick a checkpoint's estimate down with the clock, so the figure falls between
 * the backend's 25-row checkpoints rather than sitting still. Each checkpoint
 * recomputes it, which corrects any drift from the measured rate.
 */
export const countdownMs = (etaMs: number | null, computedAt: number, now: number): number | null => {
    if (etaMs == null) return null
    return Math.max(0, etaMs - Math.max(0, now - computedAt))
}

export const progressFromBatch = (
    batch: Pick<
        BatchRun,
        'total_count' | 'processed_count' | 'invalid_count' | 'status' | 'phase' | 'seeded_count'
    >
): { progress: number; label: string; phase: ProgressPhase } => {
    const total = count(batch.total_count)
    if (batch.phase === 'seeding') {
        const seeded = count(batch.seeded_count)
        const percentage = total > 0 ? Math.round((seeded / total) * 100) : 0
        return {
            progress: Math.min(99, percentage),
            label: `${Math.min(seeded, total)} of ${total} rows prepared`,
            phase: 'seeding',
        }
    }
    const done = count(batch.processed_count) + count(batch.invalid_count)
    const percentage = total > 0 ? Math.round((done / total) * 100) : 0
    const phaseLabel = batch.phase ? PHASE_LABELS[batch.phase] : null
    return {
        progress: batch.status === 'running' ? Math.min(99, percentage) : 100,
        label: phaseLabel ?? `${Math.min(done, total)} of ${total} rows`,
        phase: batch.phase === 'deduping' ? 'deduping' : 'processing',
    }
}

/**
 * Phase preview. The real stretches are minutes long and only reachable by
 * running a paid batch, so this replays the same labels and bars from a fake
 * row count to check how the card reads.
 */
export const MOCK_PHASE_MS = 5_000
export const MOCK_TOTAL_ROWS = 500
export const MOCK_TICK_MS = 120
export const MOCK_FILE_NAME = 'phase-preview.xlsx'
export const MOCK_PHASES = ['verifying', 'deduping', 'seeding', 'processing'] as const

export type MockPhase = (typeof MOCK_PHASES)[number]

export interface MockFrame {
    phase: MockPhase | 'done'
    verifying: boolean
    progress: number
    label: string
    /** Seconds into the preview, for the elapsed readout. */
    elapsedSeconds: number
}

/** Frame for a given point in the preview, so the driver holds no phase logic. */
export const mockProgressAt = (elapsedMs: number): MockFrame => {
    const index = Math.floor(Math.max(0, elapsedMs) / MOCK_PHASE_MS)
    const within = (Math.max(0, elapsedMs) % MOCK_PHASE_MS) / MOCK_PHASE_MS
    const elapsedSeconds = Math.max(0, elapsedMs) / 1000
    const phase = MOCK_PHASES[index]

    if (!phase) {
        return { phase: 'done', verifying: false, progress: 100, label: 'Finished', elapsedSeconds }
    }
    // Verify + duplicate check share the spinner panel — no bar, no pills.
    if (phase === 'verifying' || phase === 'deduping') {
        return {
            phase,
            verifying: true,
            progress: 0,
            label: phase === 'deduping' ? 'Checking for duplicates' : 'Checking rows and columns',
            elapsedSeconds,
        }
    }

    const rows = Math.round(within * MOCK_TOTAL_ROWS)
    const { progress, label } = progressFromBatch({
        total_count: MOCK_TOTAL_ROWS,
        processed_count: phase === 'processing' ? rows : 0,
        invalid_count: 0,
        status: 'running',
        phase: phase === 'processing' ? null : phase,
        seeded_count: phase === 'seeding' ? rows : 0,
    })
    return { phase, verifying: false, progress, label, elapsedSeconds }
}
